/*
 * This site fetches full pages via plain XHR for its SPA navigation, so the
 * response always carries the correct per-page <title>, meta description,
 * canonical link, Open Graph/Twitter tags, and JSON-LD for the URL requested.
 * Without this, only <title> gets synced on navigation, so anything reading
 * the live DOM after a client-side transition (share widgets, browser
 * extensions like Pinterest's clipper) sees stale metadata from whichever
 * page was last hard-loaded, misattributing content to the wrong page.
 */
export function updateHeadMeta (newHead) {
  const newTitle = newHead.querySelector('title')
  if (newTitle) document.title = newTitle.textContent

  const metaKey = el => el.hasAttribute('property') ? 'property' : 'name'

  const newMetaTags = [...newHead.querySelectorAll('meta[name], meta[property]')]
  const newKeys = new Set(newMetaTags.map(el => `${metaKey(el)}=${el.getAttribute(metaKey(el))}`))

  // Update or insert every meta tag present on the new page.
  newMetaTags.forEach(newMeta => {
    const attr = metaKey(newMeta)
    const key = newMeta.getAttribute(attr)
    const existing = document.head.querySelector(`meta[${attr}="${key}"]`)

    if (existing) {
      existing.setAttribute('content', newMeta.getAttribute('content'))
    } else {
      document.head.appendChild(newMeta.cloneNode(true))
    }
  })

  // Remove meta tags that don't exist on the new page (e.g. an optional
  // description set on one entry but empty on another).
  document.head.querySelectorAll('meta[name], meta[property]').forEach(el => {
    const attr = metaKey(el)
    const key = `${attr}=${el.getAttribute(attr)}`
    if (!newKeys.has(key)) el.remove()
  })

  // Canonical URL.
  const newCanonical = newHead.querySelector('link[rel="canonical"]')
  const existingCanonical = document.head.querySelector('link[rel="canonical"]')
  if (newCanonical) {
    if (existingCanonical) {
      existingCanonical.setAttribute('href', newCanonical.getAttribute('href'))
    } else {
      document.head.appendChild(newCanonical.cloneNode(true))
    }
  } else if (existingCanonical) {
    existingCanonical.remove()
  }

  // JSON-LD structured data is page-specific, so replace it wholesale.
  document.head.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove())
  newHead.querySelectorAll('script[type="application/ld+json"]').forEach(el => {
    document.head.appendChild(el.cloneNode(true))
  })
}
